import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { QuestionOutlineItem as QuestionOutlineItemContract } from '@/contracts/question-groups'
import type { QuestionGroupUiContract } from '@/contracts/questionnaire'
import { useAuth } from '@/lib/auth'
import { hasFormAdminAccess } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import { useFormBuilderStore } from '@/stores/formBuilderStore'
import type { Question } from '@/types/form'
import { DndContext, type DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Copy,
  CopyPlus,
  GripVertical,
  LockKeyhole,
  Trash2,
} from 'lucide-react'
import { forwardRef, useMemo, useState } from 'react'
import type { CSSProperties, HTMLAttributes } from 'react'
import { CopyQuestionToGroupDialog } from './CopyQuestionToGroupDialog'
import { getQuestionTypeMeta } from './question-type-meta'

function outlineItemId(item: QuestionOutlineItemContract) {
  return item.kind === 'question' ? `question:${item.questionId}` : `group:${item.groupInstanceId}`
}

export function QuestionOutlineList() {
  const { user } = useAuth()
  const canManage = hasFormAdminAccess(user)
  const questions = useFormBuilderStore((state) => state.questions)
  const questionGroups = useFormBuilderStore((state) => state.questionGroups)
  const questionOutline = useFormBuilderStore((state) => state.questionOutline)
  const selectedQuestionId = useFormBuilderStore((state) => state.selectedQuestionId)
  const selectQuestion = useFormBuilderStore((state) => state.selectQuestion)
  const duplicateQuestion = useFormBuilderStore((state) => state.duplicateQuestion)
  const deleteQuestion = useFormBuilderStore((state) => state.deleteQuestion)
  const updateQuestionGroup = useFormBuilderStore((state) => state.updateQuestionGroup)
  const deleteQuestionGroup = useFormBuilderStore((state) => state.deleteQuestionGroup)
  const reorderQuestionOutline = useFormBuilderStore((state) => state.reorderQuestionOutline)
  const reorderGroupQuestions = useFormBuilderStore((state) => state.reorderGroupQuestions)
  const [pendingDeleteQuestion, setPendingDeleteQuestion] = useState<Question | null>(null)
  const [pendingDeleteGroupId, setPendingDeleteGroupId] = useState<string | null>(null)
  const [copyQuestion, setCopyQuestion] = useState<Question | null>(null)
  const questionById = useMemo(
    () => new Map(questions.map((question) => [question.id, question])),
    [questions],
  )
  const visibleOutline = useMemo(() => {
    const used = new Set<string>()
    const normalized = questionOutline.filter((item) => {
      const valid =
        item.kind === 'question'
          ? questionById.has(item.questionId)
          : questionGroups.some((group) => group.id === item.groupInstanceId)
      if (!valid) return false
      const id = outlineItemId(item)
      if (used.has(id)) return false
      used.add(id)
      return true
    })
    const outlinedQuestionIds = new Set(
      normalized.filter((item) => item.kind === 'question').map((item) => item.questionId),
    )
    const outlinedGroupIds = new Set(
      normalized.filter((item) => item.kind === 'group').map((item) => item.groupInstanceId),
    )
    const missingQuestions = questions
      .filter((question) => !question.groupId && !outlinedQuestionIds.has(question.id))
      .map((question) => ({
        kind: 'question' as const,
        questionId: question.id,
        groupInstanceId: null,
      }))
    const missingGroups = questionGroups
      .filter((group) => !outlinedGroupIds.has(group.id))
      .map((group) => ({ kind: 'group' as const, groupInstanceId: group.id }))
    return [...normalized, ...missingQuestions, ...missingGroups]
  }, [questionById, questionGroups, questionOutline, questions])
  const sortableIds = visibleOutline.map(outlineItemId)
  const pendingDeleteGroup = questionGroups.find((group) => group.id === pendingDeleteGroupId)

  function onDragEnd(event: DragEndEvent) {
    if (!canManage) return
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = sortableIds.indexOf(String(active.id))
    const newIndex = sortableIds.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    reorderQuestionOutline(arrayMove(sortableIds, oldIndex, newIndex))
  }

  function confirmDeleteQuestion() {
    if (!pendingDeleteQuestion) return
    deleteQuestion(pendingDeleteQuestion.id)
    setPendingDeleteQuestion(null)
  }

  function confirmDeleteGroup() {
    if (!pendingDeleteGroupId) return
    deleteQuestionGroup(pendingDeleteGroupId)
    setPendingDeleteGroupId(null)
  }

  return (
    <>
      <div className="space-y-3">
        {visibleOutline.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-300 p-4 text-sm leading-6 text-slate-500">
            目前尚無問題。請點擊左上角加號新增題目或題組。
          </div>
        ) : (
          <DndContext onDragEnd={onDragEnd}>
            <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {visibleOutline.map((item, index) => {
                  if (item.kind === 'question') {
                    const question = questionById.get(item.questionId)
                    if (!question) return null
                    return (
                      <SortableOutlineQuestion
                        key={outlineItemId(item)}
                        itemId={outlineItemId(item)}
                        question={question}
                        index={index}
                        selected={selectedQuestionId === question.id}
                        canManage={canManage}
                        onSelect={() => selectQuestion(question.id)}
                        onDuplicate={() => duplicateQuestion(question.id)}
                        onCopyToGroup={() => setCopyQuestion(question)}
                        onDelete={() => setPendingDeleteQuestion(question)}
                      />
                    )
                  }

                  const group = questionGroups.find(
                    (candidate) => candidate.id === item.groupInstanceId,
                  )
                  if (!group) return null
                  const groupQuestions =
                    group.mode === 'synced'
                      ? (group.questions ?? [])
                      : (group.questionIds
                          .map((questionId) => questionById.get(questionId))
                          .filter(Boolean) as Question[])
                  return (
                    <SortableOutlineGroup
                      key={outlineItemId(item)}
                      itemId={outlineItemId(item)}
                      index={index}
                      group={group}
                      questions={groupQuestions}
                      selectedQuestionId={selectedQuestionId}
                      canManage={canManage}
                      onToggle={() =>
                        updateQuestionGroup(group.id, { collapsed: !group.collapsed })
                      }
                      onTitleChange={(title) => updateQuestionGroup(group.id, { title })}
                      onSelectQuestion={(questionId) => {
                        if (group.mode === 'synced') return
                        selectQuestion(questionId)
                      }}
                      onDuplicateQuestion={(questionId) => duplicateQuestion(questionId)}
                      onDeleteQuestion={(question) => setPendingDeleteQuestion(question)}
                      onDeleteGroup={() => setPendingDeleteGroupId(group.id)}
                      onMoveQuestion={(questionId, direction) => {
                        if (group.mode === 'synced') return
                        const currentIndex = group.questionIds.indexOf(questionId)
                        const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
                        if (
                          currentIndex < 0 ||
                          targetIndex < 0 ||
                          targetIndex >= group.questionIds.length
                        ) {
                          return
                        }
                        const nextIds = [...group.questionIds]
                        const [movedId] = nextIds.splice(currentIndex, 1)
                        nextIds.splice(targetIndex, 0, movedId)
                        reorderGroupQuestions(group.id, nextIds)
                      }}
                    />
                  )
                })}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      <CopyQuestionToGroupDialog
        question={copyQuestion}
        open={Boolean(copyQuestion)}
        onOpenChange={(open) => {
          if (!open) setCopyQuestion(null)
        }}
      />

      <AlertDialog
        open={Boolean(pendingDeleteQuestion)}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteQuestion(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>確定要移除「{pendingDeleteQuestion?.title}」？</AlertDialogTitle>
            <AlertDialogDescription>
              此操作會從目前問卷移除此問題。同步題組題目不會在問卷內移除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                取消
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button type="button" variant="destructive" onClick={confirmDeleteQuestion}>
                確認移除
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={Boolean(pendingDeleteGroupId)}
        onOpenChange={(open) => {
          if (!open) setPendingDeleteGroupId(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>移除「{pendingDeleteGroup?.title}」題組？</AlertDialogTitle>
            <AlertDialogDescription>
              同步題組只會移除引用；複本題組會連同此問卷內的複本題目一起移除。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                取消
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button type="button" variant="destructive" onClick={confirmDeleteGroup}>
                確認移除
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function SortableOutlineQuestion({
  itemId,
  question,
  index,
  selected,
  canManage,
  onSelect,
  onDuplicate,
  onCopyToGroup,
  onDelete,
}: {
  itemId: string
  question: Question
  index: number
  selected: boolean
  canManage: boolean
  onSelect: () => void
  onDuplicate: () => void
  onCopyToGroup: () => void
  onDelete: () => void
}) {
  const sortable = useSortable({ id: itemId, disabled: !canManage })
  return (
    <QuestionCard
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
      }}
      question={question}
      index={index}
      selected={selected}
      canManage={canManage}
      dragAttributes={sortable.attributes}
      dragListeners={sortable.listeners}
      onSelect={onSelect}
      onDuplicate={onDuplicate}
      onCopyToGroup={onCopyToGroup}
      onDelete={onDelete}
    />
  )
}

function SortableOutlineGroup({
  itemId,
  index,
  group,
  questions,
  selectedQuestionId,
  canManage,
  onToggle,
  onTitleChange,
  onSelectQuestion,
  onDuplicateQuestion,
  onDeleteQuestion,
  onDeleteGroup,
  onMoveQuestion,
}: {
  itemId: string
  index: number
  group: QuestionGroupUiContract
  questions: Question[]
  selectedQuestionId: string | null
  canManage: boolean
  onToggle: () => void
  onTitleChange: (title: string) => void
  onSelectQuestion: (questionId: string) => void
  onDuplicateQuestion: (questionId: string) => void
  onDeleteQuestion: (question: Question) => void
  onDeleteGroup: () => void
  onMoveQuestion: (questionId: string, direction: 'up' | 'down') => void
}) {
  const sortable = useSortable({ id: itemId, disabled: !canManage })
  const readonly = group.mode === 'synced'

  return (
    <div
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
      }}
      className="rounded-lg border border-emerald-200 bg-emerald-50/60 shadow-sm"
      data-testid={`question-group-card-${group.id}`}
    >
      <div className="flex items-start gap-2 p-3">
        <span
          className={cn('mt-1 shrink-0 text-slate-400', canManage && 'cursor-grab')}
          aria-label="拖拉排序"
          {...sortable.attributes}
          {...sortable.listeners}
        >
          <GripVertical className="size-4" />
        </span>
        <button
          type="button"
          className="mt-0.5 shrink-0 rounded p-1 text-slate-500 hover:bg-white"
          onClick={onToggle}
          aria-label={group.collapsed ? '展開題組' : '收合題組'}
        >
          {group.collapsed ? (
            <ChevronRight className="size-4" />
          ) : (
            <ChevronDown className="size-4" />
          )}
        </button>
        <div className="min-w-0 flex-1">
          {readonly || !canManage ? (
            <div className="truncate text-sm font-semibold text-slate-950">{group.title}</div>
          ) : (
            <Input
              value={group.title}
              onChange={(event) => onTitleChange(event.target.value)}
              className="h-8 border-0 bg-transparent px-0 font-semibold shadow-none focus-visible:ring-1"
              aria-label="題組標題"
            />
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <Badge variant={readonly ? 'warning' : 'secondary'}>
              {readonly ? '同步題組' : '複本題組'}
            </Badge>
            <span>{questions.length} 題</span>
            <span># {index + 1}</span>
          </div>
          {readonly && (
            <p className="mt-2 flex items-start gap-1 text-xs leading-5 text-amber-700">
              <LockKeyhole className="mt-0.5 size-3.5 shrink-0" />
              題目需回到題組管理修改；問卷內只可排序題組與設定跳轉。
            </p>
          )}
        </div>
        {canManage && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            onClick={onDeleteGroup}
            aria-label="移除題組"
          >
            <Trash2 className="size-4" />
          </Button>
        )}
      </div>
      {!group.collapsed && (
        <div className="space-y-2 border-t border-slate-200 p-3">
          {questions.length === 0 ? (
            <div className="rounded-md border border-dashed border-slate-300 bg-white p-3 text-sm text-slate-500">
              這個題組目前沒有問題。
            </div>
          ) : (
            questions.map((question, questionIndex) => (
              <QuestionCard
                key={question.id}
                question={question}
                index={questionIndex}
                selected={selectedQuestionId === question.id}
                canManage={canManage && !readonly}
                readonly={readonly}
                compact
                inGroup
                onSelect={() => onSelectQuestion(question.id)}
                onDuplicate={() => onDuplicateQuestion(question.id)}
                onDelete={() => onDeleteQuestion(question)}
                onMoveUp={() => onMoveQuestion(question.id, 'up')}
                onMoveDown={() => onMoveQuestion(question.id, 'down')}
                moveUpDisabled={questionIndex === 0}
                moveDownDisabled={questionIndex >= questions.length - 1}
              />
            ))
          )}
        </div>
      )}
    </div>
  )
}

type QuestionCardProps = {
  question: Question
  index: number
  selected: boolean
  canManage: boolean
  readonly?: boolean
  compact?: boolean
  inGroup?: boolean
  dragAttributes?: HTMLAttributes<HTMLElement>
  dragListeners?: Record<string, unknown>
  style?: CSSProperties
  onSelect: () => void
  onDuplicate: () => void
  onCopyToGroup?: () => void
  onDelete: () => void
  onMoveUp?: () => void
  onMoveDown?: () => void
  moveUpDisabled?: boolean
  moveDownDisabled?: boolean
}

const QuestionCard = forwardRef<HTMLDivElement, QuestionCardProps>(
  (
    {
      question,
      index,
      selected,
      canManage,
      readonly = false,
      compact = false,
      inGroup = false,
      dragAttributes,
      dragListeners,
      style,
      onSelect,
      onDuplicate,
      onCopyToGroup,
      onDelete,
      onMoveUp,
      onMoveDown,
      moveUpDisabled,
      moveDownDisabled,
    },
    ref,
  ) => {
    const typeMeta = getQuestionTypeMeta(question.type)
    return (
      <div
        ref={ref}
        style={style}
        className={cn(
          'group min-w-0 rounded-md border border-slate-200 bg-white p-3 transition-colors',
          selected &&
            !readonly &&
            (inGroup
              ? 'border-l-[3px] border-l-emerald-600 bg-emerald-50'
              : 'border-l-[3px] border-l-violet-600 bg-violet-50'),
          readonly && 'bg-slate-100',
          compact && 'p-2.5',
        )}
        data-testid={`question-card-${question.id}`}
      >
        <button
          type="button"
          className="w-full text-left"
          onClick={readonly ? undefined : onSelect}
        >
          <div className="flex min-w-0 items-center gap-2">
            <span
              className={cn('shrink-0 text-slate-400', canManage && dragListeners && 'cursor-grab')}
              aria-label="拖拉排序"
              {...dragAttributes}
              {...dragListeners}
            >
              <GripVertical className="size-4" />
            </span>
            <span className="grid size-6 shrink-0 place-items-center rounded bg-slate-100 text-xs font-semibold">
              {index + 1}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium" title={question.title}>
              {question.title || '未命名問題'}
            </span>
          </div>
        </button>
        <div className="mt-2 flex min-h-8 min-w-0 items-center justify-between gap-2">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2 text-left text-xs font-medium text-slate-500"
            onClick={readonly ? undefined : onSelect}
            aria-label={typeMeta.zhName}
            title={typeMeta.zhName}
          >
            <span className="grid size-6 shrink-0 place-items-center rounded-full bg-slate-100 text-slate-500">
              <typeMeta.icon className="size-3.5" />
            </span>
            <span className="h-px min-w-0 flex-1 bg-slate-100" aria-hidden="true" />
          </button>
          {readonly ? (
            <Badge variant="outline" className="shrink-0 bg-white">
              唯讀
            </Badge>
          ) : (
            <div className="pointer-events-none flex shrink-0 gap-1 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
              {onMoveUp && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`上移 ${question.title}`}
                  disabled={!canManage || moveUpDisabled}
                  onClick={onMoveUp}
                >
                  <ArrowUp className="size-4" />
                </Button>
              )}
              {onMoveDown && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`下移 ${question.title}`}
                  disabled={!canManage || moveDownDisabled}
                  onClick={onMoveDown}
                >
                  <ArrowDown className="size-4" />
                </Button>
              )}
              {onCopyToGroup && (
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`建立複本到題組 ${question.title}`}
                  disabled={!canManage}
                  onClick={onCopyToGroup}
                >
                  <CopyPlus className="size-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                aria-label={`複製 ${question.title}`}
                disabled={!canManage}
                onClick={onDuplicate}
              >
                <Copy className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`移除 ${question.title}`}
                className="text-rose-600 hover:bg-rose-50"
                disabled={!canManage}
                onClick={onDelete}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  },
)
QuestionCard.displayName = 'QuestionCard'
